import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Menu,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { Download, Search } from '@mui/icons-material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAppTranslation } from '../../i18n/useAppTranslation';
import { useOrgFeatureFlags } from '../../hooks/useOrgFeatureFlags';
import { useToast } from '../../contexts/ToastContext';
import { apiService } from '../../services/api';
import { integrationApiService } from '../../services/integrationApi';
import type {
  LightspeedScanMatchStatus,
  LightspeedScanRow,
  LightspeedScanRowList,
  LightspeedScanRowsQuery,
} from '../../services/integrationApi';
import {
  EmptyState,
  FilterPill,
  InlineMessage,
  KpiCard,
  KpiGrid,
  LoadingState,
  SectionCard,
  StatusChip,
  TabShell,
} from '../ui';
import type { StatusTone } from '../ui';

const T = 'integrations.lightspeedCatalogMatch';

const LISTS: LightspeedScanRowList[] = ['matched', 'unmatched_items', 'unmatched_products'];
const STATUSES: LightspeedScanMatchStatus[] = ['linked', 'in_selection', 'not_selected'];
const MATCHED_BY = ['upc', 'sku', 'model'] as const;
const STATUS_TONE: Record<LightspeedScanMatchStatus, StatusTone> = {
  linked: 'positive',
  in_selection: 'info',
  not_selected: 'neutral',
};
/** `/store/sync-selection/add` takes the ids in one body; big batches go in pages of this size. */
const ADD_BATCH = 500;

interface MenuPillProps {
  label: string;
  value: string | undefined;
  options: Array<{ value: string; label: string }>;
  anyLabel: string;
  clearLabel: string;
  onChange: (value: string | undefined) => void;
}

/** A FilterPill with a single-choice value menu. */
const MenuPill: React.FC<MenuPillProps> = ({ label, value, options, anyLabel, clearLabel, onChange }) => {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const [open, setOpen] = useState(false);
  const current = options.find((o) => o.value === value);
  return (
    <>
      <FilterPill
        label={label}
        valueLabel={current?.label ?? anyLabel}
        active={!!current}
        open={open}
        onOpen={() => setOpen(true)}
        anchorRef={setAnchor}
        trailing={current ? { onClick: () => onChange(undefined), title: clearLabel, ariaLabel: clearLabel } : null}
      />
      <Menu anchorEl={anchor} open={open && !!anchor} onClose={() => setOpen(false)}>
        <MenuItem selected={!current} onClick={() => { onChange(undefined); setOpen(false); }}>
          {anyLabel}
        </MenuItem>
        {options.map((o) => (
          <MenuItem key={o.value} selected={o.value === value} onClick={() => { onChange(o.value); setOpen(false); }}>
            {o.label}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

export interface LightspeedCatalogMatchProps {
  integrationId: string;
  /** Fewer rows per page, for the setup walkthrough dialog. */
  compact?: boolean;
}

/**
 * Catalog match (`lightspeed_link_only`): scan the store's Lightspeed catalog
 * read-only against Motzu, read every row of the report, add matches to the
 * sync selection and export the lists.
 */
const LightspeedCatalogMatch: React.FC<LightspeedCatalogMatchProps> = ({ integrationId, compact = false }) => {
  const { t } = useAppTranslation();
  const { showToast } = useToast();
  const queryClient = useQueryClient();
  const { isEnabled } = useOrgFeatureFlags();
  const perIntegrationSelection = isEnabled('per_integration_sync_selection');

  const [list, setList] = useState<LightspeedScanRowList>('matched');
  const [search, setSearch] = useState('');
  const [q, setQ] = useState('');
  const [status, setStatus] = useState<LightspeedScanMatchStatus | undefined>();
  const [matchedBy, setMatchedBy] = useState<'upc' | 'sku' | 'model' | undefined>();
  const [brand, setBrand] = useState<string | undefined>();
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(compact ? 10 : 50);
  const [picked, setPicked] = useState<Set<string>>(new Set());

  useEffect(() => {
    const timer = setTimeout(() => setQ(search.trim()), 300);
    return () => clearTimeout(timer);
  }, [search]);
  useEffect(() => {
    setPage(0);
  }, [list, q, status, matchedBy, brand, pageSize]);

  const scanQuery = useQuery({
    queryKey: ['lightspeed-scan', integrationId],
    queryFn: () => integrationApiService.getLightspeedScan(integrationId),
  });
  const report = scanQuery.data ?? null;
  const hasRows = !!report?.rowChunks;

  const filters: Omit<LightspeedScanRowsQuery, 'offset' | 'limit'> = useMemo(
    () => (list === 'matched' ? { list, q: q || undefined, status, matchedBy, brand } : { list, q: q || undefined }),
    [list, q, status, matchedBy, brand],
  );
  const rowsQuery = useQuery({
    queryKey: ['lightspeed-scan-rows', integrationId, report?.scannedAt, filters, page, pageSize],
    queryFn: () =>
      integrationApiService.getLightspeedScanRows(integrationId, { ...filters, offset: page * pageSize, limit: pageSize }),
    enabled: hasRows,
    placeholderData: (previous) => previous,
  });

  // The matched list reports its status counts as of now; keep the latest for the KPI.
  const [summary, setSummary] = useState<Record<LightspeedScanMatchStatus, number> | null>(null);
  const [brands, setBrands] = useState<string[]>([]);
  useEffect(() => {
    if (rowsQuery.data?.summary) setSummary(rowsQuery.data.summary);
    if (rowsQuery.data?.brands) setBrands(rowsQuery.data.brands);
  }, [rowsQuery.data]);
  useEffect(() => {
    setSummary(null);
    setPicked(new Set());
  }, [report?.scannedAt]);

  const refreshRows = () => queryClient.invalidateQueries({ queryKey: ['lightspeed-scan-rows', integrationId] });

  const scanMutation = useMutation({
    mutationFn: () => integrationApiService.scanLightspeedCatalog(integrationId),
    onSuccess: (result) => {
      queryClient.setQueryData(['lightspeed-scan', integrationId], result);
      refreshRows();
      showToast(
        t(`${T}.scanDone`, { matched: result.stats.matched ?? result.matched.length, total: result.stats.totalItems }),
        'success',
      );
    },
    onError: (err) => showToast(err instanceof Error ? err.message : t(`${T}.scanFailed`), 'error'),
  });

  const addMutation = useMutation({
    mutationFn: async (productIds: string[]) => {
      for (let i = 0; i < productIds.length; i += ADD_BATCH) {
        await apiService.addStoreProductsToSync(
          productIds.slice(i, i + ADD_BATCH),
          perIntegrationSelection ? integrationId : undefined,
        );
      }
      return productIds.length;
    },
    onSuccess: (count) => {
      setPicked(new Set());
      refreshRows();
      queryClient.invalidateQueries({ queryKey: ['setup-walkthrough-selection'] });
      showToast(t(`${T}.added`, { count }), 'success');
    },
    onError: (err) => {
      refreshRows();
      showToast(err instanceof Error ? err.message : t(`${T}.addFailed`), 'error');
    },
  });

  // Every not-selected match of the scan, across pages (the count on the button).
  const addAll = async () => {
    const ids: string[] = [];
    try {
      let offset = 0;
      for (;;) {
        const chunk = await integrationApiService.getLightspeedScanRows(integrationId, {
          list: 'matched',
          status: 'not_selected',
          offset,
          limit: 200,
        });
        // A product a catalog gate excludes would be linked and never filled, so it is not offered.
        for (const row of chunk.rows) if (row.canonicalProductId && !row.catalogGate) ids.push(row.canonicalProductId);
        offset += chunk.rows.length;
        if (chunk.rows.length === 0 || offset >= chunk.total) break;
      }
    } catch (err) {
      showToast(err instanceof Error ? err.message : t(`${T}.rowsFailed`), 'error');
      return;
    }
    if (ids.length) addMutation.mutate(Array.from(new Set(ids)));
  };

  const download = async () => {
    try {
      const blob = await integrationApiService.downloadLightspeedScanCsv(integrationId, filters as LightspeedScanRowsQuery);
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = `lightspeed-scan-${list.replace(/_/g, '-')}.csv`;
      anchor.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      showToast(err instanceof Error ? err.message : t(`${T}.downloadFailed`), 'error');
    }
  };

  if (scanQuery.isLoading) return <LoadingState />;

  const scanButton = (
    <Button variant={report ? 'outlined' : 'contained'} size="small" onClick={() => scanMutation.mutate()} disabled={scanMutation.isPending}>
      {scanMutation.isPending ? t(`${T}.scanning`) : report ? t(`${T}.rescan`) : t(`${T}.scan`)}
    </Button>
  );

  if (!report) {
    return (
      <SectionCard title={t(`${T}.title`)} subtitle={t(`${T}.subtitle`)}>
        <EmptyState title={t(`${T}.neverTitle`)} description={t(`${T}.neverBody`)} action={scanButton} />
      </SectionCard>
    );
  }

  const stats = report.stats;
  // Products linked before the scan are not rows of it; they count as matched and linked.
  const alreadyLinked = stats.alreadyLinked ?? 0;
  const matchedTotal = (stats.matched ?? report.matched.length) + alreadyLinked;
  const counts = summary ?? {
    linked: 0,
    in_selection: stats.matchedInSelection ?? 0,
    not_selected: stats.matchedNotSelected ?? 0,
  };
  const rows: LightspeedScanRow[] = rowsQuery.data?.rows ?? [];
  const pickable = rows.filter((r) => r.status === 'not_selected' && r.canonicalProductId && !r.catalogGate);
  const allPagePicked = pickable.length > 0 && pickable.every((r) => picked.has(r.canonicalProductId!));
  const togglePick = (id: string) =>
    setPicked((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  const togglePage = () =>
    setPicked((prev) => {
      const next = new Set(prev);
      for (const r of pickable) {
        if (allPagePicked) next.delete(r.canonicalProductId!);
        else next.add(r.canonicalProductId!);
      }
      return next;
    });
  const scannedAt = new Date(report.scannedAt);
  const busy = addMutation.isPending;

  const secondary = (text?: string) =>
    text ? (
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
        {text}
      </Typography>
    ) : null;

  const renderRow = (row: LightspeedScanRow, idx: number) => {
    if (list === 'matched') {
      const id = row.canonicalProductId || '';
      const canPick = row.status === 'not_selected' && !!id && !row.catalogGate;
      return (
        <TableRow key={`${row.lightspeedItemId}-${id}-${idx}`} hover>
          <TableCell padding="checkbox">
            <Checkbox size="small" disabled={!canPick || busy} checked={canPick && picked.has(id)} onChange={() => togglePick(id)} />
          </TableCell>
          <TableCell>
            <Typography variant="body2">{row.lightspeedDescription || '—'}</Typography>
            {secondary(row.lightspeedItemId ? `#${row.lightspeedItemId}` : undefined)}
          </TableCell>
          <TableCell>
            <Typography variant="body2">{row.canonicalName || '—'}</Typography>
            {secondary(row.canonicalBrand)}
          </TableCell>
          <TableCell>
            <Typography variant="body2">{row.matchedBy ? t(`${T}.matchedBy.${row.matchedBy}`) : '—'}</Typography>
            {secondary(row.matchedIdentifier)}
          </TableCell>
          <TableCell>
            {row.status && <StatusChip label={t(`${T}.status.${row.status}`)} tone={STATUS_TONE[row.status]} dot />}
            {row.catalogGate && (
              <Box sx={{ mt: 0.5 }}>
                <StatusChip label={t(`${T}.gate.${row.catalogGate.code}`, { defaultValue: t(`${T}.gate.other`) })} tone="caution" />
              </Box>
            )}
          </TableCell>
        </TableRow>
      );
    }
    const isItem = list === 'unmatched_items';
    return (
      <TableRow key={`${row.lightspeedItemId || row.canonicalProductId}-${idx}`} hover>
        <TableCell>
          <Typography variant="body2">{(isItem ? row.description : row.canonicalName) || '—'}</Typography>
          {secondary(isItem ? (row.lightspeedItemId ? `#${row.lightspeedItemId}` : undefined) : row.canonicalProductId)}
        </TableCell>
        <TableCell>{row.upc || '—'}</TableCell>
        <TableCell>{row.sku || '—'}</TableCell>
      </TableRow>
    );
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <SectionCard
        title={t(`${T}.title`)}
        subtitle={
          <>
            {t(`${T}.subtitle`)}{' '}
            {t(`${T}.lastScanned`, {
              when: scannedAt.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' }),
            })}
          </>
        }
        actions={scanButton}
      >
        {stats.catalogTruncated && <InlineMessage severity="warning">{t(`${T}.truncated`)}</InlineMessage>}
        <KpiGrid minWidth={compact ? 150 : 190}>
          <KpiCard label={t(`${T}.kpi.items`)} value={stats.totalItems.toLocaleString()} />
          <KpiCard
            label={t(`${T}.kpi.matched`)}
            value={matchedTotal.toLocaleString()}
            tone="positive"
            note={t(`${T}.kpi.matchedNote`, {
              linked: counts.linked + alreadyLinked,
              inSelection: counts.in_selection,
              notSelected: counts.not_selected,
            })}
            onClick={() => setList('matched')}
          />
          <KpiCard
            label={t(`${T}.kpi.notInMotzu`)}
            value={stats.unmatched.toLocaleString()}
            tone="muted"
            note={t(`${T}.kpi.notInMotzuNote`)}
            onClick={() => setList('unmatched_items')}
          />
          <KpiCard
            label={t(`${T}.kpi.missing`)}
            value={(stats.unmatchedProducts ?? 0).toLocaleString()}
            tone={(stats.unmatchedProducts ?? 0) > 0 ? 'caution' : 'muted'}
            note={t(`${T}.kpi.missingNote`)}
            onClick={() => setList('unmatched_products')}
          />
        </KpiGrid>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
          {t(`${T}.linkHint`)}
        </Typography>
      </SectionCard>

      {!hasRows ? (
        <InlineMessage>{t(`${T}.legacyBody`)}</InlineMessage>
      ) : (
        <SectionCard dense>
          <TabShell
            sticky={false}
            tabs={LISTS.map((key) => ({ key, label: t(`${T}.lists.${key}`) }))}
            value={list}
            onChange={setList}
          >
            <Box sx={{ px: 2, pb: 1.5, display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
              <TextField
                size="small"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={t(`${T}.filters.search`)}
                InputProps={{ startAdornment: <Search fontSize="small" color="action" sx={{ mr: 0.5 }} /> }}
                sx={{ minWidth: 240, flex: compact ? 1 : undefined }}
              />
              {list === 'matched' && (
                <>
                  <MenuPill
                    label={t(`${T}.filters.status`)}
                    value={status}
                    options={STATUSES.map((s) => ({ value: s, label: t(`${T}.status.${s}`) }))}
                    anyLabel={t(`${T}.filters.any`)}
                    clearLabel={t(`${T}.filters.clear`)}
                    onChange={(v) => setStatus(v as LightspeedScanMatchStatus | undefined)}
                  />
                  <MenuPill
                    label={t(`${T}.filters.matchedBy`)}
                    value={matchedBy}
                    options={MATCHED_BY.map((m) => ({ value: m, label: t(`${T}.matchedBy.${m}`) }))}
                    anyLabel={t(`${T}.filters.any`)}
                    clearLabel={t(`${T}.filters.clear`)}
                    onChange={(v) => setMatchedBy(v as 'upc' | 'sku' | 'model' | undefined)}
                  />
                  {brands.length > 1 && (
                    <MenuPill
                      label={t(`${T}.filters.brand`)}
                      value={brand}
                      options={brands.map((b) => ({ value: b, label: b }))}
                      anyLabel={t(`${T}.filters.any`)}
                      clearLabel={t(`${T}.filters.clear`)}
                      onChange={setBrand}
                    />
                  )}
                </>
              )}
              <Box sx={{ flex: 1 }} />
              {list === 'matched' && picked.size > 0 && (
                <Button size="small" variant="contained" disabled={busy} onClick={() => addMutation.mutate(Array.from(picked))}>
                  {busy ? t(`${T}.adding`) : t(`${T}.addSelected`, { count: picked.size })}
                </Button>
              )}
              {list === 'matched' && picked.size === 0 && counts.not_selected > 0 && (
                <Button size="small" variant="outlined" disabled={busy} onClick={addAll}>
                  {busy ? t(`${T}.adding`) : t(`${T}.addAll`, { count: counts.not_selected })}
                </Button>
              )}
              <Button size="small" startIcon={<Download fontSize="small" />} onClick={download}>
                {t(`${T}.download`)}
              </Button>
            </Box>

            {rowsQuery.isError ? (
              <InlineMessage severity="error">{t(`${T}.rowsFailed`)}</InlineMessage>
            ) : rowsQuery.isLoading ? (
              <LoadingState />
            ) : rows.length === 0 ? (
              <EmptyState size="compact" title={t(`${T}.empty`)} />
            ) : (
              <>
                <Box sx={{ overflowX: 'auto' }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        {list === 'matched' ? (
                          <>
                            <TableCell padding="checkbox">
                              <Checkbox
                                size="small"
                                disabled={pickable.length === 0 || busy}
                                checked={allPagePicked}
                                indeterminate={!allPagePicked && pickable.some((r) => picked.has(r.canonicalProductId!))}
                                onChange={togglePage}
                              />
                            </TableCell>
                            <TableCell>{t(`${T}.columns.lightspeedItem`)}</TableCell>
                            <TableCell>{t(`${T}.columns.motzuProduct`)}</TableCell>
                            <TableCell>{t(`${T}.columns.matchedOn`)}</TableCell>
                            <TableCell>{t(`${T}.columns.status`)}</TableCell>
                          </>
                        ) : (
                          <>
                            <TableCell>
                              {t(list === 'unmatched_items' ? `${T}.columns.lightspeedItem` : `${T}.columns.motzuProduct`)}
                            </TableCell>
                            <TableCell>{t(`${T}.columns.upc`)}</TableCell>
                            <TableCell>{t(`${T}.columns.sku`)}</TableCell>
                          </>
                        )}
                      </TableRow>
                    </TableHead>
                    <TableBody>{rows.map(renderRow)}</TableBody>
                  </Table>
                </Box>
                <TablePagination
                  component="div"
                  count={rowsQuery.data?.total ?? 0}
                  page={page}
                  onPageChange={(_e, next) => setPage(next)}
                  rowsPerPage={pageSize}
                  onRowsPerPageChange={(e) => setPageSize(Number(e.target.value))}
                  rowsPerPageOptions={compact ? [10, 25, 50] : [25, 50, 100, 200]}
                />
              </>
            )}
          </TabShell>
        </SectionCard>
      )}
    </Box>
  );
};

export default LightspeedCatalogMatch;
